/**
 * AppShell — top-level layout for the authenticated app.
 *
 * Desktop (≥768px): vertical AppSidebar on the left + content on the right.
 * Mobile (<768px) : AppSidebar is hidden (display:none in global-theme.css);
 *                   a WhatsApp-style bottom tab bar is shown instead, with
 *                   three tabs — Chats, Task, Calendar — mirroring the
 *                   primary navigation of the desktop sidebar.
 *                   Also a slim top header that respects the device
 *                   safe-area-inset-top so notches / status bars don't
 *                   clip page content.
 *
 * When a conversation is opened on mobile, Chat.tsx flips a
 * `data-conv-open` attribute on <body>; CSS uses that to hide the bottom
 * tab bar so the message thread occupies the full screen.
 */
import { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MessageSquare, ListChecks, CalendarDays, Phone, UserRoundCog } from 'lucide-react';
import AppSidebar from './AppSidebar';
import TaskNavPanel from './TaskNavPanel';
import { isTaskRoute } from './taskNav';
import EmployeeNavPanel from './EmployeeNavPanel';
import { isEmployeeRoute } from './employeeNav';
import { selectTotalUnread } from '../redux/slices/chatUnread';
import { selectUnreadCount as selectUnreadTaskCount } from '../redux/slices/notifications';
import { getMissedCount, subscribeMissed } from '../utils/call/missedBadge';
import { AttendanceClockProvider } from '../contexts/AttendanceClockContext';
import { FaceCheckGateProvider } from '../contexts/FaceCheckGateContext';

/**
 * MobileTopHeader — blank slim bar pinned at the top of the viewport
 * on phones. Padding-top includes `env(safe-area-inset-top)` so it
 * sits below the device status bar / notch and prevents page content
 * from being clipped. Hidden via CSS on desktop.
 */
function MobileTopHeader() {
  return <header className="gt-mobile-top-header" aria-hidden />;
}

function MobileBottomNav() {
  const chatUnread = useSelector(selectTotalUnread);
  const taskUnread = useSelector(selectUnreadTaskCount);
  const [missed, setMissed] = useState(getMissedCount());
  useEffect(() => subscribeMissed(() => setMissed(getMissedCount())), []);

  // These are Messenger-native or Starter-safe entry points, so every plan
  // keeps the same mobile navigation.
  const tabs = [
    { to: '/calendar', label: 'Calendar', Icon: CalendarDays, badge: 0, showDot: false },
    { to: '/employee/attendance', label: 'Employee', Icon: UserRoundCog, badge: 0, showDot: false },
    { to: '/todo', label: 'Task', Icon: ListChecks, badge: taskUnread, showDot: taskUnread > 0 },
    { to: '/calls', label: 'Calls', Icon: Phone, badge: missed, showDot: false },
    { to: '/chat', label: 'Chats', Icon: MessageSquare, badge: chatUnread, showDot: false },
  ] as const;

  return (
    <nav className="gt-mobile-bottom-nav" aria-label="Primary">
      {tabs.map(({ to, label, Icon, badge, showDot }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) =>
            `gt-mobile-bottom-nav__tab${isActive ? ' is-active' : ''}`
          }
        >
          <span className="gt-mobile-bottom-nav__icon-wrap">
            <Icon className="gt-mobile-bottom-nav__icon" />
            {badge && badge > 0 ? (
              <span className="gt-mobile-bottom-nav__badge">
                {badge > 99 ? '99+' : badge}
              </span>
            ) : showDot ? (
              <span className="gt-mobile-bottom-nav__dot" />
            ) : null}
          </span>
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell() {
  const location = useLocation();
  const onTaskRoute = isTaskRoute(location.pathname);
  const onEmployeeRoute = isEmployeeRoute(location.pathname);

  return (
    <AttendanceClockProvider>
      <FaceCheckGateProvider>
        <div className="gt-themed gt-app-shell">
          <AppSidebar />
          {/* Task Management section: a list panel opens beside the fixed icon rail
              (like the Chat conversation list), instead of the rail itself
              expanding. Desktop only — hidden on mobile via the .gt-sidebar rule. */}
          {onEmployeeRoute && <EmployeeNavPanel />}
          {onTaskRoute && <TaskNavPanel />}
          <MobileTopHeader />
          <main className="gt-app-main">
            <Outlet />
          </main>
          <MobileBottomNav />
        </div>
      </FaceCheckGateProvider>
    </AttendanceClockProvider>
  );
}
